import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import {SaveOutlined} from '@material-ui/icons';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import CharacterCounter from '../../../shared/charactercounter/CharacterCounter.jsx';
import {montarPayloadDescricao, TAMANHO_MAXIMO_DESCRICAO} from '../../components/cadastro/cadastroBasico.js';

const FormSchema = Yup.object().shape({
    descricao: Yup.string().trim()
        .required('É obrigatória')
        .max(TAMANHO_MAXIMO_DESCRICAO, `Máximo de ${TAMANHO_MAXIMO_DESCRICAO} caracteres`)
});

const VALORES_VAZIOS = {descricao: ''};

const useStyles = makeStyles(() => ({
    paper: {
        width: 500,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    }
}));

/*
 * Cria ou edita um cadastro que só tem descrição (Programa, Tipo de Habitação,
 * Tipo de Moradia). `item` vem preenchido na edição e nulo na criação. Ao
 * salvar devolve o corpo {descricao}; quem grava (e fecha o diálogo) é a tela.
 */
export default function DialogCadastroDescricao({open, onClose, nomeItem, item, salvando, onSalvar}) {
    const classes = useStyles();
    const {register, handleSubmit, reset, watch, formState: {errors}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_VAZIOS
    });

    const preencher = () => reset(item ? {descricao: item.descricao || ''} : VALORES_VAZIOS);

    useEffect(() => {
        if (open) {
            preencher();
        }
    }, [open, item]);

    const submit = values => onSalvar(montarPayloadDescricao(values));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionProps={{onEntering: preencher}}
            scroll='body'
            aria-labelledby='dialog-cadastro-descricao-titulo'
            classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-cadastro-descricao-titulo'>
                {item ? `Editar ${nomeItem}` : `Adicionar ${nomeItem}`}
            </DialogTitle>
            <form noValidate autoComplete='off' onSubmit={handleSubmit(submit)}>
                <DialogContent classes={{root: classes.contentRoot}}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LabelValue label='Descrição *'>
                                <FormControl variant='outlined' fullWidth>
                                    <OutlinedInput
                                        {...register('descricao')}
                                        autoFocus
                                        error={!!errors.descricao}
                                        inputProps={{maxLength: TAMANHO_MAXIMO_DESCRICAO}}/>
                                    {!!errors.descricao && <FormHelperText error>{errors.descricao.message}</FormHelperText>}
                                </FormControl>
                                <CharacterCounter characterQuantityMax={TAMANHO_MAXIMO_DESCRICAO} value={watch('descricao')}/>
                            </LabelValue>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={onClose}>Fechar</TextButton>
                    <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={salvando}>
                        Salvar
                    </ContainedButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}

DialogCadastroDescricao.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    nomeItem: PropTypes.string.isRequired,
    item: PropTypes.object,
    salvando: PropTypes.bool,
    onSalvar: PropTypes.func.isRequired
};
